import { Button, Checkbox, Flex, Td, Text, Textarea, Tr } from "@chakra-ui/react"
import { NSIViewer, useNSIViewerDisclosure } from "component/mapping/result/NSIViewer"
import { ChangeEvent, FC, memo, useEffect, useState } from "react"
import { FaEdit } from "react-icons/fa"
import { SmrMappedPosition, UTDMetadatas } from "type/mapping/iterationMetadatas"
import { CorrectedResult, MappingResult } from "type/mapping/result"
import { SimilarNomenclature } from "type/mapping/similarNomenclature"
import { WithId, WithStrId } from "type/withId"
import { RU_DATE_FORMAT } from "util/formatting/date"
import {
  getMappedNomenclature,
  hasMappedNomenclature,
  isNotInNSI,
  isSameAsMappedNomenclature,
  NOT_IN_NSI_NOMENCLATURE,
} from "util/validation/mappedNomenclature"

export const NO_SECTION = "Без раздела"

// Числа сметы и УПД с разделителем разрядов: деньги до копеек, количество до тысячных
// (0,001 т при двух знаках превращалось в "0 т")
const formatAmount = (value?: number, fractionDigits = 2) =>
  value?.toLocaleString(RU_DATE_FORMAT, { maximumFractionDigits: fractionDigits })

interface MappingResultRowProps {
  metadatas?: UTDMetadatas
  mappingResult: WithId<MappingResult>
  // Правка этой строки; не весь список — иначе галочка в одной строке
  // перерисовывала все строки таблицы
  correctedResult?: CorrectedResult
  onCorrectNomenclatureSelect: (result: CorrectedResult) => void
  isUTDIteration?: boolean
  isSMRIteration?: boolean
  smrPosition?: SmrMappedPosition
  isIterationApproved?: boolean
  isLoading?: boolean
}

const MappingResultRowComponent: FC<MappingResultRowProps> = (props) => {
  const {
    metadatas,
    mappingResult,
    correctedResult,
    onCorrectNomenclatureSelect,
    isUTDIteration,
    isSMRIteration,
    smrPosition,
    isIterationApproved,
    isLoading,
  } = props

  const result = mappingResult.result
  const rowNumber = result?.row_number

  const selectedNomenclature = correctedResult?.nomenclature
  const isCorrectedResultExists = !!selectedNomenclature

  const mappedNomenclature = getMappedNomenclature(mappingResult)
  const isMappedNomenclatureExists =
    mappedNomenclature && hasMappedNomenclature(mappingResult, isSMRIteration)

  const correctedNomenclatureName = correctedResult?.nomenclature?.name
  const isSelectedNotInNSI = isNotInNSI(selectedNomenclature)
  // Без сопоставления оба имени undefined — без этой проверки строка
  // выглядела отмеченной корректной
  const isMarkedAsCorrect =
    !!isMappedNomenclatureExists &&
    correctedNomenclatureName === mappedNomenclature?.nomenclature
  const isMarkAsCorrectBtnDisabled =
    !isMappedNomenclatureExists ||
    isIterationApproved ||
    isLoading ||
    !mappedNomenclature.nomenclature.trim()

  const similarMappingsList = result?.similar_mappings
  const isSimilarMappingsExists = !!similarMappingsList && similarMappingsList.length > 0

  const mappedGroup: WithStrId<SimilarNomenclature> | undefined =
    isMappedNomenclatureExists
      ? {
          id: mappedNomenclature.nomenclature_guid,
          name: mappedNomenclature.group,
          material_code: mappedNomenclature.group_code,
        }
      : undefined

  const mappedMaterialsList = metadatas?.mapped_materials
  const mappedMaterial = mappedMaterialsList?.find(
    (material) => material.number === rowNumber,
  )
  const materialQuantity = mappedMaterial?.quantity
  const unitPrice = mappedMaterial?.price // Цена за единицу товара
  const totalCost = mappedMaterial?.cost // Общая сумма (кол-во * цена за единицу)
  const vatAmount = mappedMaterial?.vat_amount // Сумма налога
  const totalCostWithVAT =
    totalCost !== undefined && vatAmount !== undefined ? totalCost + vatAmount : undefined

  const handleSelect = (selectedNomenclature?: WithStrId<SimilarNomenclature>) => {
    const result: CorrectedResult = {
      ...(correctedResult || ({} as CorrectedResult)),
      nomenclature: selectedNomenclature,
    }
    onCorrectNomenclatureSelect(result)
  }

  // Текст пишем в локальное состояние, а в общий список правок отдаём, когда
  // поле теряет фокус: иначе каждая буква перерисовывала всю таблицу, и на
  // сотнях строк ввод заметно отставал
  const savedFeedback = correctedResult?.feedback || ""
  const [feedback, setFeedback] = useState(savedFeedback)

  useEffect(() => {
    setFeedback(savedFeedback)
  }, [savedFeedback])

  const handleFeedbackSave = () => {
    if (feedback === savedFeedback) {
      return
    }

    const result: CorrectedResult = {
      ...(correctedResult || ({} as CorrectedResult)),
      feedback: !!feedback.trim() ? feedback : undefined,
    }

    onCorrectNomenclatureSelect(result)
  }

  const handleMarkAsCorrect = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isMappedNomenclatureExists) {
      return
    }

    const isChecked = e.target.checked
    if (isChecked) {
      handleSelect({
        id: mappedNomenclature.nomenclature_guid,
        name: mappedNomenclature.nomenclature,
        material_code: mappedNomenclature.material_code,
      })
    } else {
      handleSelect(undefined)
    }
  }

  const { isNSIViewerOpen, onNSIViewerOpen, onNSIViewerClose } = useNSIViewerDisclosure()

  // Смета и УПД — общий вид строки проверки
  const isReview = isSMRIteration || isUTDIteration

  return (
    <>
      <Tr
        whiteSpace="break-spaces"
        // Подсветка строки при наведении — как в истории сопоставлений
        _hover={{ bg: "gray.50" }}
        // По этим меткам фильтры прячут строки через CSS
        // (MappingResultsTable), не перерисовывая их
        data-section={smrPosition?.position.section_name || NO_SECTION}
        data-not-found={!isMappedNomenclatureExists}
        data-checked={!!correctedResult?.nomenclature}
        data-same-as-nsi={isSMRIteration && isSameAsMappedNomenclature(mappingResult, true)}
      >
        {/* Смета: номер позиции в смете — первой колонкой */}
        {isSMRIteration && (
          <Td px={2} py={2} color="gray.500" whiteSpace="nowrap">
            <Text>{smrPosition?.position.number}</Text>
          </Td>
        )}

        {/* Initial nomenclature */}
        <Td px={3} py={2}>
          <Text>{result?.nomenclature}</Text>
          {/* Раздел сметы — подписью, а не заголовком группы: таблица плоская,
              по разделу фильтруют */}
          {isSMRIteration && smrPosition?.position.section_name && (
            <Text mt={2} fontSize="xs" color="gray.500">
              {smrPosition.position.section_name}
            </Text>
          )}
        </Td>

        {/* Mapped Nomenclature */}
        <Td px={3} py={2}>
          {/* Mappings */}
          {isMappedNomenclatureExists && (
            <Flex w="full" direction="column" gap={2}>
              <Text>{mappedNomenclature.nomenclature}</Text>
            </Flex>
          )}

          {/* Модель не нашла группу или материал — показываем почему */}
          {/* Объяснение модели сметчику не показываем — оно для разбора ошибок,
              лежит в результате (llm_comment) */}
          {/* УПД: если модель предложила похожие — показываем их (ниже) */}
          {!isMappedNomenclatureExists &&
            (isSMRIteration || (isUTDIteration && !isSimilarMappingsExists)) && (
              <Text color="gray.500">Нет в НСИ</Text>
            )}

          {/* Группа НСИ — подписью, а не своей колонкой: место нужно названиям */}
          {isReview && result?.group && (
            <Text mt={2} fontSize="xs" color="gray.500">
              {result.group}
            </Text>
          )}

          {/* Similar Mappings */}
          {!isMappedNomenclatureExists && isSimilarMappingsExists && (
            <Flex direction="column" gap={2}>
              <Text fontWeight="light">
                Не нашлось номенклатуры с такими параметрами, но возможно Вы имели ввиду:
              </Text>

              {/* Nomenclatures List */}
              <Flex direction="column" gap={1}>
                {similarMappingsList.map((mapping, index) => (
                  <Text key={index}>{mapping.nomenclature}</Text>
                ))}
              </Flex>
            </Flex>
          )}
        </Td>

        {/* Nomenclature Group */}
        {!isReview && (
          <Td px={3} py={2}>
            <Text>{result?.group}</Text>
          </Td>
        )}

        {/* Is Correct Checkbox */}
        <Td px={3} py={2}>
          <Flex w="full" direction="column" justifyContent="center" alignItems="center">
            {/* Без сопоставления отмечать нечего — прочерк вместо бледного квадрата */}
            {isMappedNomenclatureExists ? (
              <Checkbox
                size="lg"
                borderColor="gray.500"
                colorScheme="green"
                isChecked={isMarkedAsCorrect}
                onChange={handleMarkAsCorrect}
                isDisabled={isMarkAsCorrectBtnDisabled}
              />
            ) : (
              <Text color="gray.400">—</Text>
            )}
          </Flex>
        </Td>

        {/* Corrected Nomenclature */}
        <Td px={3} py={2}>
          <Flex w="full" direction="column" gap={2}>
            {isCorrectedResultExists && (
              <Flex w="full" direction="column" gap={2}>
                <Text w="fit-content" whiteSpace="normal">
                  {correctedNomenclatureName}
                </Text>
              </Flex>
            )}

            <Flex w="full" direction="column" gap={2}>
              {/* <Button
              h="fit-content"
              w="fit-content"
              // variant="outline"
              colorScheme="teal"
              size="sm"
              py={1}
              onClick={handleMarkAsCorrect}
              rightIcon={<FaCheckCircle />}
              isDisabled={isMarkAsCorrectBtnDisabled}
            >
              Отметить корректной
            </Button> */}

              {/* "Нет в НСИ" — у смет многих материалов в НСИ нет (KRE-713). У УПД
                  тот же вариант есть в окне выбора из НСИ, кнопка — быстрее */}
              {isReview && !isIterationApproved && !isSelectedNotInNSI && (
                <Button
                  h="fit-content"
                  w="fit-content"
                  variant="outline"
                  colorScheme="gray"
                  size="sm"
                  py={1}
                  fontWeight="medium"
                  onClick={() => handleSelect(NOT_IN_NSI_NOMENCLATURE)}
                  isDisabled={isLoading}
                >
                  <Text>Нет в НСИ</Text>
                </Button>
              )}

              {/* NSI Viewer Btn */}
              {!isIterationApproved && (
                <Button
                  h="fit-content"
                  w="fit-content"
                  // variant="outline"
                  colorScheme="blue"
                  size="sm"
                  py={1}
                  fontWeight="medium"
                  textAlign="left"
                  rightIcon={<FaEdit />}
                  onClick={onNSIViewerOpen}
                  isDisabled={isLoading}
                >
                  <Text>Выбрать из НСИ</Text>
                </Button>
              )}
            </Flex>
          </Flex>
        </Td>

        {/* Feedback */}
        {/* Вся ячейка — поле ввода: без рамки и отступов, подсветка только в фокусе */}
        <Td p={0} h="1px" minW="220px">
          <Textarea
            h="full"
            minH="full"
            w="full"
            resize="none"
            border="none"
            borderRadius={0}
            bg="transparent"
            px={3}
            py={2}
            _hover={{ bg: "blackAlpha.50" }}
            _focusVisible={{
              bg: "white",
              boxShadow: "inset 0 0 0 2px var(--chakra-colors-gray-300)",
            }}
            placeholder="Подскажите, а почему мы неправильно сопоставили?"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            onBlur={handleFeedbackSave}
          />
        </Td>

        {isSMRIteration && (
          <>
            {/* Количество с единицей измерения, цена за единицу и сумма — как у УПД */}
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>
                {formatAmount(smrPosition?.position.real_plan, 3)}{" "}
                {smrPosition?.position.measure}
              </Text>
            </Td>
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>{formatAmount(smrPosition?.position.price)}</Text>
            </Td>
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>{formatAmount(smrPosition?.position.cost_plan)}</Text>
            </Td>
          </>
        )}

        {isUTDIteration && (
          <>
            {/* Количество, цена за единицу и сумма с НДС — как у сметы */}
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>{formatAmount(materialQuantity, 3)}</Text>
            </Td>
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>{formatAmount(unitPrice)}</Text>
            </Td>
            <Td px={2} py={2} isNumeric whiteSpace="nowrap" fontSize="sm">
              <Text>{formatAmount(totalCostWithVAT)}</Text>
            </Td>
          </>
        )}
      </Tr>

      {/* NSI Viewer — создаём, только когда открыт: в каждой из сотен строк
          закрытое окно с состояниями и эффектами замедляло перерисовку */}
      {isNSIViewerOpen && (
        <NSIViewer
          prevGroup={mappedGroup}
          onNomenclatureSelect={handleSelect}
          isOpen={isNSIViewerOpen}
          onClose={onNSIViewerClose}
        />
      )}
    </>
  )
}

// memo: перерисовываются только строки, у которых что-то поменялось
export const MappingResultRow = memo(MappingResultRowComponent)
